import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI, achievementsAPI, badgesAPI, coursesAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  TextField,
  Paper,
  Grid,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import Button from '../components/UIKit/Button';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MemoryGame from './MemoryGame';
import SchoolIcon from '@mui/icons-material/School';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { toast } from 'react-toastify';

// Функция форматирования времени
const formatTime = (seconds) => {
  if (seconds === null) return '--:--';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
};

// Update Paper components with consistent rounded corners
const StyledPaper = ({ children, ...props }) => (
  <Paper
    elevation={0}
    {...props}
    sx={{
      borderRadius: '10px',
      backgroundColor: 'transparent',
      ...props.sx,
    }}
  >
    {children}
  </Paper>
);

// Segmented progress bar component
const SegmentedProgress = ({ currentStep, totalSteps }) => {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        my: 2,
      }}
    >
      {Array.from({ length: totalSteps }, (_, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: '4px',
            background: (theme) => theme.palette.primary.main,
            opacity:
              index < currentStep ? 1 : index === currentStep ? 0.7 : 0.15,
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </Box>
  );
};

const TestView = () => {
  const { testId, courseSlug, moduleSlug, lessonSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['tests', 'common']);

  const [test, setTest] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navigation, setNavigation] = useState(null);
  const [courseId, setCourseId] = useState(null);

  // Получение теста и курса
  useEffect(() => {
    const fetchTest = async () => {
      try {
        setLoading(true);
        // При загрузке нового теста сбрасываем состояние
        setSubmitted(false);
        setResult(null);
        setUserAnswers([]);
        setCurrentQuestion(0);

        // Получаем courseId по courseSlug (нужен для submit)
        if (courseSlug) {
          try {
            const courseResponse = await coursesAPI.getBySlug(courseSlug);
            setCourseId(courseResponse.data._id);
          } catch (courseError) {
            console.error('Ошибка при получении курса:', courseError);
          }
        }

        const response = lessonSlug
          ? await testsAPI.getByContext(testId, courseSlug, moduleSlug, lessonSlug)
          : await testsAPI.getById(testId);
        console.log('Получен ответ от сервера:', response);

        if (!response || !response.data) {
          console.error('Ответ не содержит данных:', response);
          throw new Error(t('tests:view.noData'));
        }

        setTest(response.data);
        console.log('Тест успешно загружен:', response.data);

        if (!response.data.timeLimit) {
          console.warn('Отсутствует timeLimit в данных теста');
        }

        setTimeLeft(response.data.timeLimit * 60); // конвертация в секунды
        console.log(
          'Установлен таймер:',
          response.data.timeLimit * 60,
          'секунд'
        );

        // Инициализация массива ответов
        const initialAnswers = response.data.questions
          ? response.data.questions.map(() => '')
          : [];
        console.log(
          'Инициализированы пустые ответы:',
          initialAnswers.length,
          'вопросов'
        );

        setUserAnswers(initialAnswers);
        setLoading(false);
      } catch (error) {
        console.error('Ошибка при загрузке теста:', error);

        // Проверка типа ошибки и более подробные сообщения
        if (error.response) {
          // Сервер вернул ответ с ошибкой
          console.error('Статус ошибки:', error.response.status);
          console.error('Данные ошибки:', error.response.data);

          // Пользовательское сообщение в зависимости от статуса
          if (error.response.status === 404) {
            setError(t('tests:view.notFound'));
          } else if (error.response.status === 401) {
            setError(t('tests:view.unauthorized'));
            navigate('/login');
          } else {
            setError(
              `${t('tests:view.serverError')}: ${
                error.response.data.message || t('tests:view.unknownError')
              }`
            );
          }
        } else if (error.request) {
          // Запрос был сделан, но ответ не получен
          console.error(
            'Запрос отправлен, но ответ не получен:',
            error.request
          );
          setError(t('tests:view.noResponse'));
        } else {
          // Произошла ошибка при настройке запроса
          console.error('Ошибка настройки запроса:', error.message);
          setError(`${t('tests:view.loadError')}: ${error.message}`);
        }

        setLoading(false);
      }
    };

    if (testId) {
      fetchTest();
    }
  }, [testId, navigate, t, courseSlug, moduleSlug, lessonSlug]);

  
  // Отправка ответов
  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Отправляемые ответы:', userAnswers);

      // Вычисляем затраченное время (в секундах)
      const timeSpent = test.timeLimit * 60 - timeLeft;
      console.log('Затраченное время (сек):', timeSpent);

      const response = await testsAPI.submit(testId, userAnswers, timeSpent, courseId);
      
      // Получаем информацию о навигации СНАЧАЛА (до отображения результатов)
      try {
        if (courseSlug && moduleSlug) {
          // Новый API с параметрами курса и модуля
          const navResponse = await testsAPI.getNavigation(courseSlug, moduleSlug, testId);
          setNavigation(navResponse.data);
        } else {
          console.warn('Недостаточно параметров для получения навигации');
        }
      } catch (navError) {
        console.error('Ошибка при получении информации о навигации:', navError);
      }

      setResult(response.data);
      setSubmitted(true);

      // Проверяем достижения после завершения теста
      try {
        const achievementResponse = await achievementsAPI.check();
        if (achievementResponse.data.newAchievements && achievementResponse.data.newAchievements.length > 0) {
          console.log('Получены новые достижения:', achievementResponse.data.newAchievements);
        }
      } catch (achievementError) {
        console.error('Ошибка при проверке достижений:', achievementError);
      }

      // Проверяем значки после завершения теста
      try {
        const badgesResponse = await badgesAPI.check();
        if (badgesResponse.data.newBadges && badgesResponse.data.newBadges.length > 0) {
          console.log('Получены новые значки:', badgesResponse.data.newBadges);
          toast.success(t('check_badges.new_badges', { count: badgesResponse.data.newBadges.length }));
        }
      } catch (badgesError) {
        console.error('Ошибка при проверке значков:', badgesError);
      }

      setLoading(false);
    } catch (error) {
      console.error('Ошибка при отправке ответов:', error);
      setError(t('tests:view.submitError'));
      setLoading(false);
    }
  }, [userAnswers, test?.timeLimit, timeLeft, testId, courseId, courseSlug, moduleSlug, t]);

  // Таймер для теста
  useEffect(() => {
    let timer;
    if (timeLeft > 0 && !submitted) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timeLeft, submitted, handleSubmit]);

  // Обработка изменения ответа
  const handleAnswerChange = (value) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestion] = value;
    setUserAnswers(newAnswers);
  };

  // Обработка чекбоксов для вопросов с несколькими вариантами ответов
  const handleMultipleChoiceChange = (option) => {
    const currentAnswer = userAnswers[currentQuestion] || '';
    const answers = currentAnswer.split(',').filter((a) => a.trim());

    if (answers.includes(option)) {
      const newAnswers = answers.filter((a) => a !== option).join(',');
      handleAnswerChange(newAnswers);
    } else {
      const newAnswers = [...answers, option].join(',');
      handleAnswerChange(newAnswers);
    }
  };

  // Навигация между вопросами
  const goToNextQuestion = () => {
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Обработчик для повторного прохождения теста
  const handleRetakeTest = () => {
    // Сброс состояния для повторного прохождения
    setSubmitted(false);
    setResult(null);
    setCurrentQuestion(0);
    setTimeLeft(test.timeLimit * 60);
    setUserAnswers(test.questions.map(() => ''));
  };

  // Обработчик для перехода к следующему тесту или уроку
  const handleNavigateNext = () => {
    if (!navigation) return;

    if (navigation.nextTest) {
      // Используем правильный формат URL для тестов - переходим на preview страницу
      // Если есть nextModule, используем его slug, иначе текущий модуль
      const targetModuleSlug = navigation.nextModule?.slug || navigation.moduleSlug;
      navigate(`/courses/${navigation.courseSlug}/modules/${targetModuleSlug}/tests/${navigation.nextTest.id}`);
    } else if (navigation.nextLesson) {
      // Если урок находится в другом модуле, используем nextModule, иначе текущий модуль
      const targetModuleSlug = 
        navigation.nextModule?.slug || navigation.moduleSlug;
      const nextLessonSlug = navigation.nextLesson.slug || navigation.nextLesson.id;

      navigate(
        `/courses/${navigation.courseSlug}/modules/${targetModuleSlug}/lessons/${nextLessonSlug}`
      );
    } else {
      // Если нет следующего элемента, вернуться к курсу
      navigate(`/courses/${navigation.courseSlug}`);
    }
  };

  // Рендер формы для ответа на вопрос
  const renderQuestionForm = () => {
    if (!test || !test.questions[currentQuestion]) return null;

    const question = test.questions[currentQuestion];
    const userAnswer = userAnswers[currentQuestion] || '';

    return (
      <Box>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: 'text.primary',
              }}
            >
              {question.question}
            </Typography>
          </FormLabel>

          {question.image && (
            <Box sx={{ my: 2, textAlign: 'center' }}>
              <img
                src={question.image}
                alt="Изображение к вопросу"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
            </Box>
          )}

          {renderQuestionOptions(question, userAnswer)}
        </FormControl>
      </Box>
    );
  };

  // Рендер вариантов ответа в зависимости от типа вопроса
  const renderQuestionOptions = (question, userAnswer) => {
    switch (question.type) {
      case 'single-choice':
        return (
          <RadioGroup
            value={userAnswer}
            onChange={(e) => handleAnswerChange(e.target.value)}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option.text}
                control={<Radio />}
                label={option.text}
                sx={{
                  backgroundColor: (theme) => theme.palette.background.paper,
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.divider,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '12px',
                  marginLeft: 0,
                  marginRight: 0,
                  width: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: (theme) => theme.palette.primary.main,
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              />
            ))}
          </RadioGroup>
        );

      case 'multiple-choice':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {question.options.map((option, index) => {
              const isChecked = userAnswer.split(',').includes(option.text);
              return (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={() => handleMultipleChoiceChange(option.text)}
                    />
                  }
                  label={option.text}
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    marginLeft: 0,
                    marginRight: 0,
                    width: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: (theme) => theme.palette.primary.main,
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                />
              );
            })}
          </Box>
        );

      case 'true-false':
        return (
          <RadioGroup
            value={userAnswer}
            onChange={(e) => handleAnswerChange(e.target.value)}
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label={t('common:status.true')}
              sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '12px',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: (theme) => theme.palette.primary.main,
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label={t('common:status.false')}
              sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '12px',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: (theme) => theme.palette.primary.main,
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            />
          </RadioGroup>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            variant="outlined"
            value={userAnswer}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder={t('tests:view.answerPlaceholder')}
            multiline
            rows={3}
          />
        );

      case 'open-answer':
        return (
          <Box>
            <TextField
              fullWidth
              variant="outlined"
              value={userAnswer}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder={t('tests:view.openAnswerPlaceholder', 'Введіть вашу відповідь...')}
              multiline
              rows={6}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {t('tests:view.openAnswerHint', 'Ця відповідь буде перевірена вчителем або AI')}
            </Typography>
          </Box>
        );

      case 'memory':
        return (
          <MemoryGame
            options={question.options}
            onChange={handleAnswerChange}
          />
        );

      default:
        return (
          <Typography color="error">{t('tests:view.unknownType')}</Typography>
        );
    }
  };

  if (loading && !test) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('tests:view.loading')}</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: '10px' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: '10px' }}>
          {t('tests:view.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <StyledPaper sx={{ p: 3 }}>
      {/* Title and Description - always visible */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            {test.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {test.description}
          </Typography>
        </Grid>

        {/* Timer and Progress - only visible during test */}
        {!submitted && (
          <>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'flex-start', md: 'flex-end' },
                  gap: 1,
                }}
              >
                <Typography
                  variant="h5"
                  color={timeLeft < 60 ? 'error.main' : 'text.primary'}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AccessTimeIcon /> {formatTime(timeLeft)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  {t('tests:view.passingScore')}: {test.passingScore}%
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mb: 2, pt: '0 !important' }}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'right',
                  color: 'text.primary',
                }}
              >
                {t('tests:view.question')} {currentQuestion + 1}{' '}
                {t('tests:view.of')} {test.questions.length}
              </Typography>
              <Box>
                <SegmentedProgress
                  currentStep={currentQuestion}
                  totalSteps={test.questions.length}
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>

      {/* Результаты */}
      {submitted && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mt: 4,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: (theme) =>
                result.hasPendingReview
                  ? theme.palette.warning.main + '33'
                  : result.passed
                    ? theme.palette.success.main + '33'
                    : theme.palette.secondary.main + '33',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            {result.hasPendingReview ? (
              <HourglassEmptyIcon
                sx={{
                  fontSize: 40,
                  color: (theme) => theme.palette.warning.main,
                }}
              />
            ) : (
              <SchoolIcon
                sx={{
                  fontSize: 40,
                  color: (theme) =>
                    result.passed
                      ? theme.palette.success.main
                      : theme.palette.secondary.main,
                }}
              />
            )}
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: '600',
              color: (theme) =>
                result.hasPendingReview
                  ? theme.palette.warning.main
                  : result.passed
                    ? theme.palette.success.main
                    : theme.palette.secondary.main,
              mb: 1,
            }}
          >
            {result.hasPendingReview
              ? t('tests:results.pending_review')
              : t(result.passed ? 'tests:results.passed' : 'tests:results.failed')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {result.hasPendingReview
              ? t('tests:results.pending_review_message')
              : result.passed
                ? t('tests:results.passed_message')
                : t('tests:results.failed_message')}
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1">
              {t('tests:results.score')}{' '}
              <Typography
                component="span"
                variant="h4"
                sx={{
                  fontWeight: '600',
                  color: (theme) =>
                    result.hasPendingReview
                      ? theme.palette.warning.main
                      : result.passed
                        ? theme.palette.success.main
                        : theme.palette.secondary.main,
                  mx: 1,
                }}
              >
                {Math.round(result.score)}%
              </Typography>{' '}
              {result.hasPendingReview
                ? t('tests:results.pending_review_score_note')
                : t('tests:results.passingScore', { score: test.passingScore })}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleRetakeTest}
              sx={{
                minWidth: 160,
                height: 48,
              }}
            >
              {t('tests:results.try_again')}
            </Button>

            {result.passed &&
            navigation &&
            (navigation.nextTest || navigation.nextLesson) ? (
              <Button
                variant="contained"
                onClick={handleNavigateNext}
                sx={{
                  minWidth: 200,
                  height: 48,
                }}
              >
                {navigation.nextTest
                  ? t('tests:results.next_test')
                  : t('tests:results.next_lesson')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `/courses${navigation ? `/${navigation.courseSlug}` : ''}`
                  )
                }
                sx={{
                  minWidth: 200,
                  height: 48,
                }}
              >
                {t('tests:results.return_to_courses')}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Форма вопроса */}
      {!submitted && (
        <>
          <Box>{renderQuestionForm()}</Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2} justifyContent="space-between">
            <Grid item>
              <Button
                variant="outlined"
                onClick={goToPrevQuestion}
                disabled={currentQuestion === 0}
              >
                {t('tests:view.prev')}
              </Button>
            </Grid>

            <Grid item>
              {currentQuestion < test.questions.length - 1 ? (
                <Button variant="contained" onClick={goToNextQuestion}>
                  {t('tests:view.next')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                >
                  {t('tests:view.finish')}
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </StyledPaper>
  );
};

export default TestView;
